<template>
  <div
    data-testid="doctor-card-button"
    role="button"
    tabindex="0"
    class="group relative rounded-2xl border border-gray-200 bg-white overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-primary20 hover:-translate-y-0.5"
    @click="navigateToDetail"
    @keydown.enter="navigateToDetail"
    @keydown.space.prevent="navigateToDetail"
  >
    <div class="h-1" style="background: linear-gradient(to right, #2f7f91, #3ca2b9)" />

    <div class="flex flex-col items-center px-4 pt-4 pb-3">
      <div class="relative shrink-0" :style="{ width: svgSize + 'px', height: svgSize + 'px' }">
        <svg
          :width="svgSize"
          :height="svgSize"
          class="absolute top-0 left-0 -rotate-90"
          aria-hidden="true"
        >
          <circle
            :cx="svgSize / 2"
            :cy="svgSize / 2"
            :r="radius"
            :stroke="NEUTRAL_RING_COLOR"
            :stroke-width="STROKE_WIDTH"
            fill="none"
          />
          <circle
            v-if="clampedScore > 0"
            :cx="svgSize / 2"
            :cy="svgSize / 2"
            :r="radius"
            :stroke="arcColor"
            :stroke-width="STROKE_WIDTH"
            fill="none"
            :stroke-dasharray="circumference"
            :stroke-dashoffset="offset"
            stroke-linecap="round"
          />
        </svg>
        <v-avatar
          color="primary"
          :size="AVATAR_SIZE"
          class="absolute"
          :style="{ top: STROKE_WIDTH + 'px', left: STROKE_WIDTH + 'px' }"
        >
          <ProtectedAvatar :src="avatarSrc" :size="AVATAR_SIZE">
            <span class="text-lg font-bold">{{ initials }}</span>
          </ProtectedAvatar>
        </v-avatar>
      </div>

      <p
        class="mt-2 text-sm font-semibold text-gray-900 text-center truncate w-full"
        :title="doctorName"
      >
        {{ doctorName }}
      </p>

      <p class="mt-0.5 text-xs text-gray-500 text-center truncate w-full" :title="specialtyLabel">
        {{ specialtyLabel }}
      </p>
    </div>

    <div class="border-t border-gray-100 px-4 py-2.5 flex flex-col gap-1.5">
      <div class="flex items-center gap-2 text-xs text-gray-600">
        <v-icon size="14" color="primary30">{{ mdiCardAccountDetailsOutline }}</v-icon>
        <span class="font-medium">CRM {{ crmDisplay }}</span>
      </div>

      <div class="flex items-center gap-2 text-xs text-gray-600">
        <v-icon size="14" color="primary30">{{ mdiOfficeBuildingOutline }}</v-icon>
        <span class="truncate capitalize font-medium">{{ companyLabel }}</span>
      </div>
    </div>

    <div v-if="isPending" class="border-t border-gray-100 px-4 py-2">
      <LabelBadge
        :label="inviteStatus!.label"
        :color="inviteStatus!.color"
        :icon="null"
        size="small"
      />
    </div>

    <div
      class="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
    >
      <v-icon size="16" color="grey-lighten-1">{{ mdiChevronRight }}</v-icon>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { mdiCardAccountDetailsOutline, mdiOfficeBuildingOutline, mdiChevronRight } from '@mdi/js'

import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import ProtectedAvatar from '@/components/designSystem/AvatarUser/ProtectedAvatar.vue'
import type { DoctorGroupedResponse } from '@/Network/Types/Responses/CompanyResponses'
import { CompanyDoctorInviteStatusEnum } from '@/Network/Types/Requests/CompanyRequests'
import { classifyNpsScore, NEUTRAL_RING_COLOR } from '@/utils/nps'
import {
  getInviteStatusConfig,
  getInitials,
  resolveAvatarUrl,
  toTitleCase
} from '../../helpers/doctorStatus'

const props = defineProps<{
  doctor: DoctorGroupedResponse
}>()

const router = useRouter()

const navigateToDetail = () => {
  router.push({ name: 'doctor-detail', params: { doctorId: props.doctor.doctor.id } })
}

const doctorName = computed(() =>
  toTitleCase(props.doctor.doctor.user?.name || props.doctor.doctor.mainCrm.name)
)

const specialtyLabel = computed(() => {
  const list = props.doctor.doctor.specialties.map(toTitleCase)
  return list.length ? list.join(', ') : 'Sem especialidade'
})

const avatarSrc = computed(() => resolveAvatarUrl(props.doctor.doctor.user?.avatarUrl))

const initials = computed(() => getInitials(doctorName.value))

const AVATAR_SIZE = 48
const STROKE_WIDTH = 4
const radius = AVATAR_SIZE / 2 + STROKE_WIDTH / 2
const svgSize = radius * 2 + STROKE_WIDTH
const circumference = 2 * Math.PI * radius

const clampedScore = computed(() => {
  const score = props.doctor.doctor.npsScore
  if (score == null) return 0
  return Math.min(100, Math.max(0, (score + 100) / 2))
})

const offset = computed(() => circumference * (1 - clampedScore.value / 100))

const arcColor = computed(() => {
  const score = props.doctor.doctor.npsScore
  if (score == null) return NEUTRAL_RING_COLOR
  return classifyNpsScore(score).color
})

const crmDisplay = computed(
  () => `${props.doctor.doctor.mainCrm.number}-${props.doctor.doctor.mainCrm.uf}`
)

const companyLabel = computed(() => {
  const companies = props.doctor.companies
  if (companies.length === 0) return 'Sem vínculo'
  if (companies.length === 1) return companies[0].company.name.toLowerCase()
  return `${companies.length} empresas`
})

const inviteStatus = computed(() => getInviteStatusConfig(props.doctor.companies[0]?.doctorStatus))

const isPending = computed(
  () => props.doctor.companies[0]?.doctorStatus === CompanyDoctorInviteStatusEnum.PENDING
)
</script>
